import { Dispatch, SetStateAction, useState } from "react"
import { CreateOrderDialogFlow, CreateOrderDialogMode, CreateOrderDialogType } from "./createOrderTypes"
import { fetchOrderDetails, OrderDetailsResponse } from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import { OutlookBookingSuccessArgs } from "../../../modules/order/pages/outlookCreateOrderTypes"
import { InvalidUserPermissionsDialog } from "./orderOnBehalf/InvalidUserPermissionsDialog"
import { UserWeekMenu } from "../../../modules/nonAdminUser/reports/home/weekMenuApi"
import { CreateOrderDialogContent } from "./mainForm/CreateOrderDialogContent"
import { CreateOrderDialogWrap } from "./components/CreateOrderDialogWrap"
import { patchedOrderDetails } from "./mainForm/patchedOrderDetails"
import { OnBehalfCompletedDialog } from "./hooks/useCreateOrderUser"
import { UserType } from "../../../modules/login/loginTypes"
import { getOrderPerson } from "../../utils/getOrderPerson"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { OrderContextState } from "./mainForm/helpers"
import { CreateOrderType } from "./helpers"

export const transformOrderDetails = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)

type CreateOrderDialogControllerProps = Readonly<{
    open: boolean
    handleClose: () => void
    activeOrderTab?: CreateOrderType
    dialogType: CreateOrderDialogType
    orderId?: number
    mode: CreateOrderDialogMode
    flowConfig?: CreateOrderDialogFlow
    isOnBehalfCompleted: OnBehalfCompletedDialog
    setIsOnBehalfCompleted: Dispatch<SetStateAction<OnBehalfCompletedDialog>>
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
}>

export function CreateOrderDialogController({
    open,
    handleClose,
    activeOrderTab,
    dialogType,
    orderId,
    mode,
    flowConfig,
    isOnBehalfCompleted,
    setIsOnBehalfCompleted,
    handleOutlookBookingCompleted,
}: CreateOrderDialogControllerProps) {
    const [showInvalidUserPermissions, setShowInvalidUserPermissions] = useState(false)
    const [orderContext, setOrderContext] = useState<OrderContextState>({ mode, orderId })

    const queryEnabled = Boolean(orderContext?.orderId) && mode !== "create"

    const { data: baseOrderDetails, isFetching } = useApiQuery(fetchOrderDetails, {
        queryName: UserWeekMenu.USER_MEETING_SERVICE_DETAILS,
        dependencies: {
            orderId: orderContext?.orderId!,
            flow: flowConfig?.vstoFlowParam,
        },
        enabled: queryEnabled,
        select: transformOrderDetails,
    })

    const handleInvalidUserPermissions = () => {
        setShowInvalidUserPermissions(false)
        setIsOnBehalfCompleted((prev) => {
            return { ...prev, show: true }
        })
    }

    const isLoadingOrderDetails = queryEnabled && isFetching && !baseOrderDetails

    const orderDetails = !isLoadingOrderDetails
        ? patchedOrderDetails({
              flowConfigOrder: flowConfig?.order,
              orderDetails: baseOrderDetails,
          })
        : undefined
    const { creator } = getOrderPerson(orderDetails?.organizers)

    return (
        <>
            <CreateOrderDialogWrap
                onBehalfOf={isOnBehalfCompleted.userType != UserType.USER}
                open={open}
                onClose={handleClose}
                flowConfig={flowConfig}
            >
                {!isLoadingOrderDetails && (
                    <CreateOrderDialogContent
                        flowConfig={flowConfig}
                        handleClose={handleClose}
                        user={{
                            userId: creator?.id ?? isOnBehalfCompleted.userId,
                            displayName: creator?.name ?? isOnBehalfCompleted.displayName,
                            companyId: isOnBehalfCompleted.companyId,
                            userType: isOnBehalfCompleted.userType,
                        }}
                        activeOrderTab={activeOrderTab}
                        dialogType={dialogType}
                        handleInvalidUserPermissions={() => setShowInvalidUserPermissions(true)}
                        orderContext={orderContext}
                        setOrderContext={setOrderContext}
                        orderDetails={orderDetails}
                        handleOutlookBookingCompleted={handleOutlookBookingCompleted}
                    />
                )}
            </CreateOrderDialogWrap>
            {showInvalidUserPermissions && (
                <InvalidUserPermissionsDialog
                    isOnBehalfCompleted={isOnBehalfCompleted}
                    activeOrderTab={activeOrderTab}
                    handleClose={handleClose}
                    handleShowOrderOnBehalfDialog={handleInvalidUserPermissions}
                />
            )}
        </>
    )
}
